// One payment in full: what bill it settles, whose money it is, how it
// arrived and (for money the office took by hand) who recorded it.

import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Skeleton, SkeletonGroup } from "../../../components/chrome/Skeleton";
import { SEP } from "../../../components/chrome/SepList";
import { usePageTrail } from "../../../components/chrome/trail";
import { billingStep } from "../lib/billingTrail";
import { useCurrentUser } from "../../../components/auth/CurrentUserContext";
import { isAdminRole } from "../../../lib/roles";
import { ModuleOffNotice } from "../../../components/chrome/ModuleOffNotice";
import { downloadReceiptPdf } from "../api/billing";
import { useBillingPayment } from "../hooks/useBilling";
import { fmtCents } from "../../../lib/money";
import { METHOD_LABEL, PAYMENT_LABEL, PAYMENT_PILL, day } from "../lib/billingLabels";

export default function PaymentDetail() {
  const { paymentId } = useParams<{ paymentId: string }>();
  const q = useBillingPayment(paymentId);
  const isAdmin = isAdminRole(useCurrentUser()?.role);
  usePageTrail([billingStep(isAdmin), { label: "Payment" }]);
  const [downloadError, setDownloadError] = useState<string | null>(null);

  if (q.isLoading) {
    return (
      <main className="cc-main">
        <div className="cc-body">
          <SkeletonGroup label="Loading payment">
            <Skeleton width="30%" height={22} />
            <Skeleton width="100%" height={220} radius={10} />
          </SkeletonGroup>
        </div>
      </main>
    );
  }

  if (q.isError) {
    return (
      <main className="cc-main">
        <div className="cc-body">
          <div className="cc-card" style={{ padding: "12px 18px", color: "var(--warn-600)" }}>
            Could not reach the server. Reload to try again.
          </div>
        </div>
      </main>
    );
  }

  if (!q.data) {
    return (
      <main className="cc-main">
        <div className="cc-body">
          <div className="cc-card" style={{ padding: "12px 18px", color: "var(--muted)" }}>
            This payment does not exist - the link may be stale or mistyped.
          </div>
        </div>
      </main>
    );
  }

  const p = q.data.payment;

  return (
    <main className="cc-main">
      <div className="cc-body">
        <ModuleOffNotice id="billing" name="Billing" />
        <div className="cc-index-head">
          <span className="title">
            {fmtCents(p.amountCents)}
            {"  "}
            <span style={{ display: "inline-flex", gap: 8 }}>
              {p.disputeStatus === "open" && <span className="cc-pill crit">Disputed</span>}
              <span className={`cc-pill ${PAYMENT_PILL[p.status] ?? "muted"}`}>
                {PAYMENT_LABEL[p.status] ?? p.status}
              </span>
            </span>
          </span>
          <span className="blurb">
            {p.provider === "manual"
              ? `${METHOD_LABEL[p.method ?? "other"]} payment taken by the office`
              : "Online payment through Stripe"}
          </span>
        </div>

        {/* Sandbox money reconciles to nothing. It says so once, where the
            decision to count it would be made. */}
        {p.livemode === false && (
          <div className="cc-card" style={{ padding: "10px 18px", color: "var(--warn-600)" }}>
            Test payment - taken on a sandbox Stripe key, so no money moved and the bill is not
            settled.
          </div>
        )}

        <div className="cc-card">
          <dl className="cc-facts">
            <dt>Client</dt>
            <dd>{p.clientLabel ?? "Unknown client"}</dd>
            <dt>Pays the bill</dt>
            <dd>
              {p.invoiceNumber ? (
                <Link className="cc-link-subtle" to={`/billing/invoice/${p.invoiceId}`}>
                  <span className="tnum-mono">{p.invoiceNumber}</span>
                </Link>
              ) : (
                "-"
              )}
              {p.invoiceDescription ? (
                <span style={{ color: "var(--muted)" }}>
                  {SEP}
                  {p.invoiceDescription}
                </span>
              ) : null}
            </dd>
            <dt>How it arrived</dt>
            <dd>
              {p.provider === "manual" ? METHOD_LABEL[p.method ?? "other"] : "Online (Stripe)"}
              {p.provider === "manual" && p.reference ? (
                <span className="tnum-mono"> #{p.reference}</span>
              ) : null}
            </dd>
            <dt>Date paid</dt>
            <dd className="tnum">{day(p.paidAt) || "-"}</dd>
            <dt>Recorded</dt>
            <dd className="tnum">{day(p.createdAt) || "-"}</dd>
            <dt>Recorded by</dt>
            <dd>{p.recordedByName ?? "-"}</dd>
            {p.note ? (
              <>
                <dt>Note</dt>
                <dd>{p.note}</dd>
              </>
            ) : null}
            {p.refundedAt ? (
              <>
                <dt>Refunded</dt>
                <dd className="tnum">{day(p.refundedAt)}</dd>
              </>
            ) : null}
            {p.reversedByName ? (
              <>
                <dt>Reversed by</dt>
                <dd>{p.reversedByName}</dd>
              </>
            ) : null}
            {p.provider === "stripe" && p.providerInvoiceNumber ? (
              <>
                <dt>Stripe invoice</dt>
                <dd className="tnum-mono">{p.providerInvoiceNumber}</dd>
              </>
            ) : null}
            {p.receiptUrl ? (
              <>
                <dt>Stripe receipt</dt>
                <dd>
                  <a className="cc-link-btn" href={p.receiptUrl} target="_blank" rel="noreferrer">
                    Open receipt
                  </a>
                </dd>
              </>
            ) : null}
            {p.status === "succeeded" ? (
              <>
                <dt>Receipt</dt>
                <dd>
                  <button
                    type="button"
                    className="cc-link-btn"
                    onClick={() => {
                      setDownloadError(null);
                      downloadReceiptPdf(p._id, p.invoiceNumber).catch((err) =>
                        setDownloadError(
                          err instanceof Error ? err.message : "Could not download the receipt.",
                        ),
                      );
                    }}
                  >
                    Save receipt PDF
                  </button>
                </dd>
              </>
            ) : null}
          </dl>
          {downloadError && (
            <div
              role="alert"
              style={{ padding: "0 18px 14px", fontSize: 12.5, color: "var(--red-700)" }}
            >
              {downloadError}
            </div>
          )}
        </div>
      </div>
    </main>
  );
}
